import * as React from "react";
import dayjs from "dayjs";
import "dayjs/locale/es"; // Importa el locale español
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { DayCalendarSkeleton } from "@mui/x-date-pickers/DayCalendarSkeleton";
import { Badge, Button, IconButton, Typography } from "@mui/joy";
import { Box } from "@mui/material";
import { ArrowLeft, ArrowRight } from "@mui/icons-material";

// Configura dayjs para usar el locale español
dayjs.locale("es");

const getDayStyles = (turno) => {
  if (!turno) {
    return {};
  }

  if (!turno.dayVariant) {
    return {
      color: turno.textColor || "#ffffff",
      backgroundColor: turno.backgroundColor || "#695194",
    };
  }

  return {
    color: "#1f2328",
    backgroundColor: "#f2f4f7",
    boxShadow: "0 1px 2px rgba(0,0,0,0.16)",
    "&:hover": {
      backgroundColor: "#e8ecf1",
      boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
    },
    "&.Mui-selected": {
      backgroundColor: "#e2e7ee",
      color: "#1f2328",
      boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
    },
    "&.Mui-selected:hover": {
      backgroundColor: "#dbe1e9",
    },
  };
};

const renderBadgeContent = (turno) => {
  if (!turno) {
    return undefined;
  }

  const tieneResumenPorModalidad =
    turno.cantTurnosPresencial !== undefined ||
    turno.cantTurnosVirtual !== undefined;

  if (
    tieneResumenPorModalidad &&
    turno.cantTurnosPresencial === 0 &&
    turno.cantTurnosVirtual === 0
  ) {
    return undefined;
  }

  if (!tieneResumenPorModalidad) {
    return turno.cantTurnos > 9 ? "+9" : `${turno.cantTurnos}`;
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        lineHeight: 1,
        gap: "2px",
      }}
    >
      {turno.cantTurnosPresencial > 0 && (
        <Typography
          sx={{
            fontSize: "10px",
            color: "#b50009",
            fontWeight: 700,
            backgroundColor: "#ffe7ea",
            borderRadius: "6px",
            paddingInline: "4px",
            paddingBlock: "1px",
          }}
        >
          {`${turno.cantTurnosPresencial > 9 ? "+9" : turno.cantTurnosPresencial}`}
        </Typography>
      )}
      {turno.cantTurnosVirtual > 0 && (
        <Typography
          sx={{
            fontSize: "10px",
            color: "#1d7a48",
            fontWeight: 700,
            backgroundColor: "#e7f7ed",
            borderRadius: "6px",
            paddingInline: "4px",
            paddingBlock: "1px",
          }}
        >
          {`${turno.cantTurnosVirtual > 9 ? "+9" : turno.cantTurnosVirtual}`}
        </Typography>
      )}
    </Box>
  );
};

//* Componente personalizado para renderizar los días
function CustomDay(props) {
  const { day, outsideCurrentMonth, highlightedDates, ...other } = props;

  const turno = highlightedDates.find((turno) =>
    dayjs(turno.date).isSame(day, "day"),
  );
  const esHoy = dayjs(day).isSame(dayjs(), "day");
  const tieneResumenPorModalidad =
    turno &&
    (turno.cantTurnosPresencial !== undefined ||
      turno.cantTurnosVirtual !== undefined);

  return (
    <Badge
      key={day.toString()}
      variant="solid"
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      badgeContent={renderBadgeContent(turno)}
      sx={{
        fontSize: "8px",
        "& .MuiBadge-badge": {
          ...(tieneResumenPorModalidad
            ? {
                backgroundColor: "transparent",
                minWidth: "auto",
                height: "auto",
                padding: 0,
                boxShadow: "none",
                transform: "translate(22%, -52%)",
              }
            : {
                backgroundColor: "#b50009",
                color: "#ffffff",
                minWidth: "22px",
                height: "22px",
                padding: 0,
                borderRadius: "999px",
                fontSize: "10px",
                fontWeight: 700,
                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                transform: "translate(25%, -8%)",
              }),
        },
      }}
      size="md"
      badgeInset="10%"
      invisible={
        !turno ||
        (tieneResumenPorModalidad
          ? turno.cantTurnosPresencial === 0 && turno.cantTurnosVirtual === 0
          : !turno.cantTurnos)
      }
    >
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
        sx={{
          ...getDayStyles(turno),
          ...(esHoy && {
            backgroundColor: "#dff2ff",
            fontWeight: 700,
            "&:hover": {
              backgroundColor: "#d4edff",
            },
            "&.Mui-selected": {
              backgroundColor: "#c9e7ff",
              color: "#1f2328",
            },
            "&.Mui-selected:hover": {
              backgroundColor: "#bedfff",
            },
          }),
          borderRadius: "4px",
          border: esHoy
            ? "1px solid #9fd2f5"
            : turno
              ? "1px solid rgba(0,0,0,0.08)"
              : "none",
          fontSize: "18px",
        }}
        disabled={!turno}
      />
    </Badge>
  );
}

export default function CustomCalendar({
  value,
  onChange,
  minDate,
  maxDate,
  highlightedDates,
}) {
  const mostrarLeyendaModalidades = highlightedDates?.some(
    (turno) =>
      turno.cantTurnosPresencial !== undefined ||
      turno.cantTurnosVirtual !== undefined,
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 0.25,
        }}
      >
        <DateCalendar
          value={dayjs(value)}
          onChange={onChange}
          renderLoading={() => <DayCalendarSkeleton />}
          sx={{
            "& .MuiPickersCalendarHeader-label": {
              textTransform: "capitalize",
            },
            "& .MuiDayCalendar-monthContainer": {
              mt: 0.5,
            },
          }}
          slots={{
            day: CustomDay,
          }}
          slotProps={{
            day: {
              highlightedDates,
            },
          }}
          showDaysOutsideCurrentMonth
          minDate={minDate}
          maxDate={maxDate}
          disablePast
          dayOfWeekFormatter={(weekday) => (
            <Button disabled variant={"plain"} sx={{}}>
              <Typography
                sx={{
                  fontSize: "18px",
                  textAlign: "center",
                }}
              >
                {weekday.format("dd")}
              </Typography>
            </Button>
          )}
        />
        {mostrarLeyendaModalidades && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 1,
              mt: -0.5,
              px: 2,
              pb: 0.25,
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                borderRadius: "999px",
                backgroundColor: "#fff4f4",
                color: "#b50009",
                px: 1,
                py: 0.5,
                fontWeight: 700,
              }}
            >
              P: presencial
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                borderRadius: "999px",
                backgroundColor: "#effaf4",
                color: "#1d7a48",
                px: 1,
                py: 0.5,
                fontWeight: 700,
              }}
            >
              V: virtual
            </Typography>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
}
